import CIcon from '@coreui/icons-react';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { debounce } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFlexibleMultiModal } from '../../../../actions/common';
import { fetchSalesforceDomain, setIntentCell, setWidthColumnReports, updateIntentCell } from '../../../../actions/subscriber';
import { callTokenApi } from '../../../../apiCaller';
import {
     COMPONENT_NAME,
     REPORT_NAMES,
     REPORT_TAB_TYPES,
     REPORT_TYPE,
     SECTION_REPORT,
     SUBSCRIBER_PATH,
     DEFAULT_OBJECT_SF,
     API_AI_INSIGHTS_INTENT_PROPERTY_RERUN,
     SCOPED,
     DATE_SCOPE,
} from '../../../../constants';
import { useDidMount, usePrevious } from '../../../../helpers/customHooks';
import { camelCase, extractName, encodeObj, generateCustomDimensionName, getOriginalField, getSegmentField, getStatusTag, idSegment, formatTableName, getExternalRelationTypeByObject } from '../../../../utils';
import {
     changeSortSegment,
     convertNameSegmentSort,
     filterConversionNameItem,
     findItemByTargetId,
     findLongestValue,
     formatNumber,
     formatNumberValue,
     getDefaultFieldReport,
     handleChangeCol,
     isRateField,
     handleChangeColV2,
} from '../../../../utils/UtilReport';
import { EventExplorerContext } from './EventExplorer';
import ReportTableCompare from './ReportTableCompare';
import {
     ActiveUser,
     ActiveUserCount,
     ConversionValue,
     CustomConversionValue,
     EventCount,
     GroupedItemDisplay,
     Industries,
     IntentPropertyCell,
     LinkMetricCount,
     PeopleCount,
     PersonCreated,
     ReportDuration,
     RevealedCompany,
     SalesConversionCount,
     SecondaryConversionValueCount,
     SessionCount,
     TableCellDefault,
     TableCellValueUndefined,
     Tag,
     TagArrayFormat,
     UserCount,
     UserSourceDetails,
     ViewDetail,
     ViewProfileById,
} from './report-v2/FieldCustom';
import { ChangeDataLoading, LoadingPanel, LongLoading, RebuildSegmentLoading, ReportIsCancel } from './report-v2/LoadingTemplate';
import TooltipContentTemplate from './report-v2/TooltipContentTemplate';
import UpgradePlanText from './report-v2/UpgradePlanText';
import { CustomFooterCellDefault, CustomFooterCellDimensions, CustomFooterCellProperties } from './report-v2/grid/CustomFooterCell';
import CustomHeaderCell from './report-v2/grid/CustomHeaderCell';
import PaginationReport from './report-v2/grid/PaginationReport';
import useFetchData from './report-v2/useFetchData';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const ReportTable = () => {
     const dispatch = useDispatch();
     const {
          reportName,
          filterMetric,
          setFilterMetric,
          filterDimensions,
          setFilterDimensions,
          filterProperties,
          setFilterProperties,
          reportData,
          filterEvent,
          setIsLoading,
          isLoading,
          tableData,
          setTableData,
          sort,
          setSort,
          itemsPerPage,
          setItemsPerPage,
          scoped,
          reportType,
          conversionNameMetric,
          externalObjectMetric,
          handleGetOption,
          reportCompare,
          showData,
          enableRevealedCompanies,
          changeData,
          recordProfile,
          newProperties: propertiesAll,
          newDimensions: dimensionsAll,
          customDimensionInfo,
          dateRangeConfig,
          metricDefault,
          setMetricDefault,
          convRate,
          convName,
          setConvName,
          eCommerce,
          setECommerce,
          utmArray,
          eCoItemCategories,
          setAttributeBeforeReload,
          setChangeData,
          listSegmentReport,
          reloadTable,
          setReloadTable,
          setOffSet,
          handleFindView,
          listSegmentReportApply,
          tempScopedReport,
          listDynamicFields,
          customObjectSF,
          unRelated,
          SFObject,
          intentProperties,
          objectID,
          staticLinkKey,
          weight,
          salesForceObjects,
          customExternalObjects,
     } = useContext(EventExplorerContext);
     const customExternalObjectReport = useSelector((state) => state.subscriber.customExternalObjectReport);
     const externalObject = customExternalObjectReport.find((item) => item.id === objectID);
     const {dimensionsDefault, propertiesDefault, metricsDefault, dimensionExternal} = getDefaultFieldReport({ reportType, reportName, scoped, weight, utmArray, properties: propertiesAll, dimensions: dimensionsAll, objectID, eCoItemCategories, customExternalObjectReport, handleGetOption });
     const metricDynamic = listDynamicFields?.metrics?.length > 0 ? listDynamicFields?.metrics : [];
     const propertyDynamic = listDynamicFields?.properties?.length > 0 ? listDynamicFields?.properties : [];
     const dateRangeReports = useSelector((state) => state.subscriber.dateRangeReports);
     const widthColumnReports = useSelector((state) => state.subscriber.widthColumnReports);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const listSegmentApplyReload = useSelector((state) => state.subscriber.listSegmentApplyReload);
     const listSegmentReload = useSelector((state) => state.subscriber.listSegmentReload);
     const intentCellState = useSelector((state) => state.subscriber.intentCellState);     
     const newDimensions = handleChangeColV2(dimensionsDefault, filterDimensions);
     const newProperties = handleChangeCol(propertiesDefault, filterProperties);
     const defaultShowShortId = filterDimensions ? filterDimensions.map((item) => item.value) : [];
     const [activePage, setActivePage] = useState(1);
     const totalPages = Math.ceil(reportData.overallRowCount / itemsPerPage) || 1;
     const didMount = useDidMount();
     const [pagination, setPagination] = useState(true);
     const showLock = {};
     const [lock, setLock] = useState({});
     const tableGrid = useRef(null);

     const [showLongLoading, setShowLongLoading] = useState(false);
     const [isLoadingFetch, setIsLoadingFetch] = useState(false);
     const [reportIsCancel, setReportIsCancel] = useState(false);
     const [isLoadingTable, setIsLoadingTable] = useState(false);
     const [showShortId, setShowShortId] = useState(defaultShowShortId);
     const [isRebuildLoading, setIsRebuildLoading] = useState(false);
     const [reportIsLargeRecords, setReportIsLargeRecords] = useState(false);
     const isExploreReport = !reportType || reportType === REPORT_TYPE.SALE_FORCE || reportName === REPORT_NAMES.ECOMMERCE_PURCHASE_TRANSACTION;

     const arrShowShortIds = [
          'personId',
          'userId',
          'sessionId',
          'eventId',
          'eventECommerceId',
          'companyId',
          'formId',
          'formHostNamePathName',
          'leadID',
          'contactID',
          'accountID',
          'opportunityID',
          'quoteID',
          'orderID',
          'contractID',
          'campaignID',
          'activityEventID',
          'crmLeadID',
          'crmContactID',
          'crmAccountID',
          'crmOpportunityID',
          'crmQuoteID',
          'crmOrderID',
          'crmContractID',
          'crmCampaignID',
          'crmActivityEventID',
          'customObjectID',
          'crmCustomObjectID',
          'conversionID',
          ...(Array.isArray(dimensionExternal) && dimensionExternal.length > 0
               ? dimensionExternal.map(((el) => el?.key))
               : []),
     ];
     const [openTooltip, setOpenTooltip] = useState(false);
     const [targetElement, setTargetElement] = useState(null);
     const [positionTooltip, setPositionTooltip] = useState('right');
     const [showCopyTooltip, setShowCopyTooltip] = useState(true);
     const [isResizing, setIsResizing] = useState(false);
     const [rectCell, setRectCell] = useState({});
     const disableSort = reportName === REPORT_NAMES.ECOMMERCE_PURCHASE_FLOW;
     const previousActivePage = usePrevious(activePage);
     const previousReloadPage = usePrevious(reloadTable);
     const previousNewProperties = usePrevious(newProperties);
     const previousNewDimensions = usePrevious(newDimensions);
     const previousFilterMetric = usePrevious(filterMetric);
     let returnToPage1 = activePage !== 1 && activePage === previousActivePage && reloadTable === previousReloadPage;
     const finalActivePage = returnToPage1 ? 1 : activePage;
     const [, setQueuedRerunData] = useState([]);

     // Load intent cell loading states from session storage on component mount
     useEffect(() => {
          const loadingCells = JSON.parse(sessionStorage.getItem('intentLoadingCells') || '[]');
          if (loadingCells.length > 0) {
               const loadingCells = JSON.parse(sessionStorage.getItem('intentLoadingCells') || '[]');
               const initialState = {};
               loadingCells.forEach((uniqueId) => {
                    initialState[uniqueId] = { isVisible: true, isLoading: true };
               });
               dispatch(setIntentCell(initialState));
          }
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const debouncedRerunApiCallRef = useRef(null);
     
     // Initialize the debounced function
     useEffect(() => {
          debouncedRerunApiCallRef.current = debounce(() => {
               // Get the current state directly to ensure we have the latest data
               setQueuedRerunData(currentQueuedData => {
                    if (currentQueuedData.length === 0) return [];
                    
                    const payload = { accountId: activeAccount.id, objectProperties: currentQueuedData };
                    
                    callTokenApi(API_AI_INSIGHTS_INTENT_PROPERTY_RERUN, 'POST', payload)
                         .then((response) => {
                              if (response.status !== 200) {
                                   throw new Error(response.data.message || 'Failed to rerun properties');
                              }
                         })
                         .catch((error) => {
                              payload.objectProperties.forEach(({ objectId, propertyId }) => {
                                   dispatch(updateIntentCell({ 
                                        objectId, 
                                        propertyId, 
                                        cellState: { isLoading: false, error: error.message } 
                                   }));
                              });
                         });
                    
                    // Return empty array to clear the queue after processing
                    return [];
               });
          }, 5000);
          
          return () => {
               // Clean up by canceling any pending debounced calls when the component unmounts
               if (debouncedRerunApiCallRef.current) {
                    debouncedRerunApiCallRef.current.cancel();
               }
          };
     }, [activeAccount.id, dispatch]);

     // Function to handle intent property rerun
     const handleIntentPropertyRerun = (payload) => {
          const { objectId, propertyId } = payload;

          dispatch(updateIntentCell({ objectId, propertyId, cellState: { isLoading: true, error: '', objectId, propertyId } }));

          setQueuedRerunData(prev => ([...prev, payload]));

          if (debouncedRerunApiCallRef.current) {
               debouncedRerunApiCallRef.current();
          }
     };

     const handleRerunIconClick = ({ objectId, propertyId }) => {
          handleIntentPropertyRerun({ objectId, propertyId });
     };

     const inputFetchData = {
          setIsLoading,
          itemsPerPage,
          activePage,
          setActivePage,
          reloadTable,
          sort,
          setShowLongLoading,
          returnToPage1,
          finalActivePage,
          pagination,
          dateRangeConfig,
          setIsLoadingFetch,
          setReportIsCancel,
          listSegmentReportApply,
          setIsRebuildLoading,
          reportIsLargeRecords,
          setReportIsLargeRecords,
     };

     useFetchData(inputFetchData);

     function getWidthByKey({ data, reportType, reportName, label, key, tableData, index, isMetric, newLabel }) {
          const longestValue = tableData ? findLongestValue(tableData) : undefined;
          if (data[reportType]?.[reportName] && data[reportType][reportName][label] !== undefined) {
               return data[reportType][reportName][label];
          }
          let widthDefault = label.length * 7.2 + 65;

          if (newLabel) {
               widthDefault = label.length > '10' ? label.length * 3 + 65 : label.length * 7.2 + 65;
               return widthDefault;
          }

          if (longestValue?.[key] && longestValue[key].length * 6 + 50 > widthDefault) {
               return Math.min(
                    575,
                    (index === 0 || index === 1 || index === 2) && arrShowShortIds.includes(key) && showShortId.includes(key)
                         ? label.length * 7.7 + 70
                         : longestValue[key].length * 7 + 50
               );
          }

          if (reportName === REPORT_NAMES.ECOMMERCE_PURCHASE_TRANSACTION && key === 'userSourcePath') {
               return widthDefault + 135;
          }

          if (isMetric) {
               return widthDefault + 45;
          }

          if (key === 'pSegments') {
               return widthDefault + 100;
          }

          return widthDefault;
     }

     //Function tooltip
     const removeElementTooltipScroll = () => {
          if ('ontouchstart' in window || navigator.maxTouchPoints) {
               const el = document.querySelector('[id^="tippy-"]');
               if (el) {
                    el.remove();
               }
          }
     };

     const showTooltip = (element, isHiddenCopy) => {
          isHiddenCopy ? setShowCopyTooltip(false) : setShowCopyTooltip(true);
          setOpenTooltip(true);
          setTargetElement(element.current);
     };

     useEffect(() => {
          const handleOutsideClick = (event) => {
               if (event.target.attributes.getNamedItem('title') === null && event.target.getAttribute('class') !== 'icon-copy') {
                    setOpenTooltip(false);
               }
          };

          const hideTooltip = () => {
               setOpenTooltip(false);
          };

          const handleCopyEvent = (e) => {
               const selection = window.getSelection();
               if (selection.toString().trim()) {
                    e.preventDefault();
                    const trimmedText = selection.toString().trim();
                    e.clipboardData.setData('text/plain', trimmedText);
               }
          };

          document.addEventListener('click', handleOutsideClick);
          document.addEventListener('scroll', hideTooltip);
          document.addEventListener('copy', handleCopyEvent);

          // scroll events inside Kendo Grid (.k-grid-content) don't bubble to document,
          // so attach directly to close the tooltip on horizontal/vertical table scroll
          const gridContent = tableGrid.current?._element?.querySelector('.k-grid-content');
          if (gridContent) {
               gridContent.addEventListener('scroll', hideTooltip);
          }

          return () => {
               document.removeEventListener('click', handleOutsideClick);
               document.removeEventListener('scroll', hideTooltip);
               document.removeEventListener('copy', handleCopyEvent);
               if (gridContent) {
                    gridContent.removeEventListener('scroll', hideTooltip);
               }
          };
     }, []);

     const changePositionTooltip = (props) => {
          let position = 'right';
          let left = props.mouseLeft + 10;
          let top = props.mouseTop - props.element.clientHeight / 2;

          if (rectCell && newDimensions[0] && arrShowShortIds.includes(newDimensions[0].key)) {
               left = rectCell.left + rectCell.width / 2 + 20;
               top = rectCell.top - props.element.clientHeight / 2 + 15;
          }

          if (props.mouseLeft + 360 > document.body.clientWidth) {
               left = props.mouseLeft - (props.element.clientWidth + 10);
               position = 'left';
          }

          setPositionTooltip(position);
          return { left, top };
     };

     useEffect(() => {
          const metricReport = [...conversionNameMetric, ...metricsDefault, ...externalObjectMetric, ...metricDynamic];
          if (sort?.fieldName) {
               const sortValue = listSegmentReportApply.length > 0 ? convertNameSegmentSort({ sort, listSegmentReportApply }) : sort.fieldName;
               if (filterMetric[0] && !disableSort) {
                    if (!filterMetric.find((el) => el.value === sortValue) && metricReport.find((el) => el.key === sortValue)) {
                         setSort({
                              fieldName: changeSortSegment({ sort, listSegmentReportApply, filterMetric }),
                              isDesc: sort?.fieldName ? sort.isDesc : true,
                         });
                    }

               } else if (filterMetric.length === 0 && metricReport.find((el) => el.key === sortValue && !disableSort)) {
                    setSort({});
               }
          } else if (filterMetric[0] && filterMetric.length === metricReport.length) {
               setSort({
                    fieldName: changeSortSegment({ sort, listSegmentReportApply, filterMetric }),
                    isDesc: true,
               });
          }
          // eslint-disable-next-line
     }, [filterMetric]);

     useEffect(() => {
          const sortValue =
               sort?.fieldName
                    ? listSegmentReportApply.length > 0
                         ? convertNameSegmentSort({ sort, listSegmentReportApply })
                         : sort.fieldName
                    : '';
          if (sort?.fieldName && !filterMetric.find((_item) => _item.value === sortValue)) {
               if (![...filterDimensions, ...filterProperties, ...filterMetric].find((_item) => _item.value === sortValue)) {
                    setSort({});
               }
          }
     }, [filterDimensions, filterProperties]); // eslint-disable-line

     const areEqualShuffled = (arr1 = [], arr2 = []) => {
          if (arr1.length !== arr2.length) {
               return false;
          }

          let usedIndices = new Set();

          return arr1.every((item1) => {
               return arr2.some((item2, index) => {
                    const isEqual = JSON.stringify(item1) === JSON.stringify(item2);
                    if (isEqual && !usedIndices.has(index)) {
                         usedIndices.add(index);
                         return true;
                    }
                    return false;
               });
          });
     };

     useEffect(() => {
          let checkFetch = false;

          if (filterMetric && previousFilterMetric) {
               checkFetch = filterMetric.some((el) => !previousFilterMetric.find((item) => item.value === el.value));
          }

          if (newProperties && previousNewProperties && !checkFetch) {
               checkFetch = newProperties.some((el) => !previousNewProperties.find((item) => item.key === el.key));
          }

          if (newDimensions && previousNewDimensions && !checkFetch) {
               checkFetch = !areEqualShuffled(previousNewDimensions, newDimensions);
               if (!areEqualShuffled(previousNewDimensions, newDimensions)) {
                    handleReturnPage();
                    if (didMount) {
                         setShowShortId(defaultShowShortId);
                         setChangeData(true);
                    }
               }
          }

          if (checkFetch && didMount) {
               setAttributeBeforeReload({
                    dimensions: [],
                    properties: [],
                    metrics: [],
                    scoped: '',
               });
               setReloadTable(!reloadTable);
          }
     }, [filterProperties, filterMetric, filterDimensions]); // eslint-disable-line

     const onPaginationChange = (i) => {
          setOffSet((i - 1) * itemsPerPage);
          setActivePage(i);
     };

     useEffect(() => {
          const observeCallback = (mutationsList) => {
               mutationsList.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                         // Handle the style change here
                         mutation.target.removeAttribute('style');
                    }
               });
          };

          const observeElements = () => {
               const observer = new MutationObserver(observeCallback);
               const elements = document.querySelectorAll('.k-table');
               elements.forEach((element) => {
                    observer.observe(element, {
                         attributeFilter: ['style'],
                         attributeOldValue: true,
                    });
               });

               // Clean up the observer when the component unmounts
               return () => {
                    observer.disconnect();
               };
          };

          observeElements();
     }, []);

     const handleReturnPage = () => {
          if (tableGrid.current) {
               tableGrid.current.containerRef.current.scrollTo({ top: 0 });
               const table = tableGrid.current._element.querySelectorAll('.k-table');
               table.forEach((item) => {
                    item.removeAttribute('style');
               });
          }
          setActivePage(1);
          setOffSet(0);
     };
     useEffect(() => {
          handleReturnPage();
     }, [filterEvent, sort, dateRangeReports, unRelated]); // eslint-disable-line react-hooks/exhaustive-deps
     useEffect(() => {
          if (
               (newProperties && previousNewProperties && newProperties.length > previousNewProperties.length) ||
               (filterMetric && previousFilterMetric && filterMetric.length > previousFilterMetric.length)
          ) {
               handleReturnPage();
          }
     }, [filterProperties, filterMetric]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          if (!pagination) {
               if (activePage === 1) setTableData([...reportData.rows]);
               else setTableData((old) => [...old, ...reportData.rows]);
          } else {
               setTableData([...reportData.rows]);
          }
          setIsLoadingTable(false);
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [reportData?.rows, pagination, scoped]);

     useEffect(() => {
          const conversionMetric = handleGetOption({ option: conversionNameMetric });
          const [filterDefaultItems, otherItems, eCommerceItems] = filterConversionNameItem(filterMetric, conversionMetric, listSegmentReportApply);
          if (
               [
                    REPORT_TYPE.ATTRIBUTES,
                    REPORT_TYPE.ACQUISITION,
                    REPORT_TYPE.CONTENT,
                    REPORT_TYPE.ENGAGEMENT,
                    REPORT_TYPE.CONVERSIONS,
                    REPORT_TYPE.ECOMMERCE,
                    REPORT_TYPE.SALES_CONVERSION,
               ].includes(reportType) ||
               !reportType
          ) {
               setMetricDefault(filterDefaultItems);
               setConvName(otherItems);
               setECommerce(eCommerceItems);
          }
     }, [scoped, filterMetric, listSegmentReport]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleColumnResize = (props) => {
          const foundItem = findItemByTargetId(props.columns, props.targetColumnId);
          if (foundItem && props.end) {
               dispatch(
                    setWidthColumnReports(
                         Object.assign({}, widthColumnReports, {
                              [reportType]: Object.assign({}, widthColumnReports[reportType], {
                                   [reportName]: Object.assign(
                                        {},
                                        widthColumnReports[reportType]?.[reportName]
                                             ? widthColumnReports[reportType][reportName]
                                             : {},
                                        {
                                             [foundItem.title]: foundItem.width,
                                        }
                                   ),
                              }),
                         })
                    )
               );

               setTimeout(() => {
                    setIsResizing(false);
               }, 100);
          } else {
               setIsResizing(true);
          }
     };
     const handleScrollTable = (event) => {
          const e = event.nativeEvent;
          if (e.target.scrollTop + 20 >= e.target.scrollHeight - e.target.clientHeight) {
               if (activePage < totalPages) {
                    setActivePage((activePageOld) => ++activePageOld);
               }
          }
     };

     const debouncedHandleScrollTable = debounce((event) => {
          handleScrollTable(event);
     }, 100);

     const handleChangePagination = () => {
          setPagination(!pagination);
          setItemsPerPage(20);
          setTableData([]);
          if (pagination) setActivePage(1);
          setOffSet(0);
     };

     useEffect(() => {
          if (listSegmentReload) {
               setActivePage(1);
          }
     }, [listSegmentReload]);

     const onChangeItemsPerPage = (e) => {
          setActivePage(1);
          setOffSet(0);
          setItemsPerPage(+e.target.value);
     };

     const SkeletonLoader = () => {
          const colNum = filterProperties.length + filterDimensions.length + filterMetric.length;
          return (
               <tr>
                    <td colSpan={colNum}>{/* <Skeleton shape={'text'} style={{ width: '100%' }} /> */}</td>
               </tr>
          );
     };

     const handleLinkToOtherReport = (fieldKey, linkedData, field) => {
          let path = '';
          let linkedReportData;
          let filters = [];
          const {
               id,
               conversionId,
               landingPage,
               exitPage,
               userSourceFirst,
               userSourceLast,
               userSource,
               conversionName,
               userId,
               personId,
               sessionId,
               companyId,
          } = linkedData;
          const segmentClick = field && listSegmentReportApply?.find((item) => field.includes(camelCase(item.id)));
          const externalField = metricDynamic.find((item) => item.key === fieldKey && item.section === SECTION_REPORT.EXTERNAL_OBJECTS);
          const customExternalField = customExternalObjectReport.find((item) => formatTableName({ name: item?.object, prefix: '', isObject: true }) === fieldKey.replace('lifeTime', ''));
          const externalMetricField = externalObjectMetric.find((item) => item.key === fieldKey);
          const isConversion = [REPORT_NAMES.BY_CONVERSION, REPORT_NAMES.BY_PROPERTY].includes(reportName)
          const isSalesConversion = reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION;
          let isNewCount = false;
          let isBlocked = false;
          let isCustomExternalPath = false;
          switch (fieldKey) {
               case 'eventCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_EVENTS;
                    if (reportName === REPORT_NAMES.COMPANY_DETAILS) {
                         filters.push({
                              type: 'companyId',
                              value: companyId,
                         });
                         break;
                    }
                    if (reportName === REPORT_NAMES.SESSION_DETAILS) {
                         filters.push({
                              type: 'sessionId',
                              value: sessionId,
                         });
                         break;
                    }
                    if (reportName === REPORT_NAMES.USER_DETAILS) {
                         filters.push({
                              type: 'userId',
                              value: userId,
                         });
                         break;
                    }
                    if (reportName === REPORT_NAMES.PERSON_DETAILS) {
                         filters.push({
                              type: 'personId',
                              value: personId,
                         });
                         break;
                    }
                    if (reportName === REPORT_NAMES.CONVERSION_DETAILS) {
                         filters.push({
                              type: 'conversionId',
                              value: conversionId,
                         });
                    }

                    break;
               case 'personCreatedCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_PEOPLE;
                    filters.push({
                         type: 'personId',
                         value: id,
                    });

                    break;
               case 'sessionCount':
               case 'lifeTimeSessionCount':
               case 'createdSessionCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_SESSIONS;
                    if (reportName === REPORT_NAMES.USER_DETAILS) {
                         filters.push({
                              type: 'userId',
                              value: userId,
                         });
                         break;
                    }
                    if (reportName === REPORT_NAMES.PERSON_DETAILS) {
                         filters.push({
                              type: 'personId',
                              value: personId,
                         });
                         break;
                    }
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                         });
                    });

                    break;

               case 'newSessionCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_SESSIONS;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newUserCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newPeopleCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_PEOPLE;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newCompanyCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_REVEALED_COMPANIES;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'userCount':
               case 'lifeTimeUserCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                         });
                    });
                    break;
               case 'revealedCompany':
               case 'lifeTimeCompany':
               case 'companyCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_REVEALED_COMPANIES;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                              dateScope: scoped === SCOPED.PEOPLE ? DATE_SCOPE.CREATION_DATE: dateRangeConfig?.dateScope
                         });
                    });

                    break;
               case 'lifeTimePeopleCount':
               case 'peopleCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_PEOPLE;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                              dateScope: scoped === SCOPED.COMPANY ? DATE_SCOPE.CREATION_DATE: dateRangeConfig?.dateScope
                         });
                    });

                    break;

               case 'activeUser':
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;

                    if (landingPage) {
                         filters.push({
                              type: 'landingPage',
                              value: landingPage,
                         });
                    }

                    if (exitPage) {
                         filters.push({
                              type: 'exitPage',
                              value: exitPage,
                         });
                    }

                    break;
               case 'activeUserCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;

                    if (userSourceFirst) {
                         filters.push({
                              type: 'userSourceFirst',
                              value: userSourceFirst,
                         });
                    }

                    if (userSourceLast) {
                         filters.push({
                              type: 'userSourceLast',
                              value: userSourceLast,
                         });
                    }

                    if (userSource) {
                         filters.push({
                              type: 'userSourceSession',
                              value: userSource,
                         });
                    }
                    if (conversionName) {
                         filters.push({
                              type: 'conversionName',
                              value: conversionName,
                         });
                    }
                    break;
               case 'lifeTimeLeadsCount':
               case 'lifeTimeContactsCount':
               case 'lifeTimeAccountsCount':
               case 'lifeTimeOpportunitiesCount':
               case 'lifeTimeCampaignsCount':
               case 'lifeTimeEventsCount':
               case 'lifeTimeQuotesCount':
               case 'lifeTimeOrdersCount':
               case 'lifeTimeContractsCount':
               case 'lifeTimeAccountsParentCount':
               case `${externalField?.key}`:
                    switch (fieldKey) {
                         case 'lifeTimeLeadsCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_LEADS;
                              break;
                         case 'lifeTimeContactsCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTACTS;
                              break;
                         case 'lifeTimeAccountsCount':
                         case 'lifeTimeAccountsParentCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ACCOUNTS;
                              break;
                         case 'lifeTimeOpportunitiesCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_OPPORTUNITIES;
                              break;
                         case 'lifeTimeCampaignsCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CAMPAIGNS;
                              break;
                         case 'lifeTimeEventsCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CRM_EVENTS;
                              break;
                         case 'lifeTimeQuotesCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_QUOTES;
                              break;
                         case 'lifeTimeOrdersCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ORDERS;
                              break;
                         case 'lifeTimeContractsCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTRACTS;
                              break;
                         case `${externalField?.key}`:
                              if([REPORT_TYPE.CUSTOM_EXTERNAL].includes(reportType) && customExternalField){
                                   path = `/:secondId/explore-data/custom-external-${formatTableName({name: customExternalField?.object, prefix: '',isObject: true}).toLowerCase()}`;
                                   break;
                              }
                              path = `/:secondId/explore-data/${externalField?.type.toLowerCase().replace('crm', 'crm-')}`;
                              break;
                         default:
                              break;
                    }
                    
                       if ([REPORT_TYPE.CUSTOM_EXTERNAL].includes(reportType)) {
                          const relations = getExternalRelationTypeByObject(formatTableName({
                              name: customExternalField?.object,
                              prefix: '',
                              isObject: true,
                         }), formatTableName({
                              name: externalObject?.object,
                              prefix: '',
                              isObject: true,
                         }), [customExternalField, externalObject]);
                         newDimensions.forEach((item) => {
                              filters.push({
                                   type: item.key,
                                   value: `${linkedData[item.key]}`,
                                   relations: relations,
                              });
                         });
                    } else {
                         newDimensions.forEach((item, index) => {
                              filters.push({
                                   type: item.key,
                                   value: `${linkedData[item.key]}`,
                              });
                         });
                    }

                    break;
               case `${externalMetricField?.key}`:
                    switch (fieldKey) {
                         case 'activeLeadCount':
                         case 'newLeadCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_LEADS;
                              break;
                         case 'activeContactCount':
                         case 'newContactCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTACTS;
                              break;
                         case 'activeAccountCount':
                         case 'newAccountCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ACCOUNTS;
                              break;
                         case 'activeOpportunityCount':
                         case 'newOpportunityCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_OPPORTUNITIES;
                              break;
                         case 'activeCampaignCount':
                         case 'newCampaignCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CAMPAIGNS;
                              break;
                         case 'activeEventCount':
                         case 'newEventCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CRM_EVENTS;
                              break;
                         case 'activeQuoteCount':
                         case 'newQuoteCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_QUOTES;
                              break;
                         case 'activeOrderCount':
                         case 'newOrderCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ORDERS;
                              break;
                         case 'activeContractCount':
                         case 'newContractCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTRACTS;
                              break;
                         case `active${externalMetricField?.object}Count`:
                         case `new${externalMetricField?.object}Count`:
                              path = `/:secondId/explore-data/crm-${externalMetricField?.object}`;
                              break;
                         default:
                              break;
                    }
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                              object: externalMetricField?.object,
                         });
                    });
                    isNewCount = fieldKey.includes('new')

                    break;
               case 'conversionCount':
                    if (isSalesConversion) {
                         // Determine path based on primaryObject from row data
                         const primaryObj = extractName(linkedData.primaryObjectClick);
                         if (primaryObj) {
                              // Check if it's a custom external object
                              const isCustomExternal = customExternalObjectReport.some(
                                   (item) => item?.object === primaryObj
                              );

                              if (isCustomExternal) {
                                   // Custom External Object → custom-external-{name} path
                                   path = `/:secondId/explore-data/custom-external-${formatTableName({ name: primaryObj, prefix: '', isObject: true }).toLowerCase()}`;
                                   isCustomExternalPath = true;
                              } else if (DEFAULT_OBJECT_SF[primaryObj]) {
                                   // Standard CRM object
                                   path = `/:secondId/explore-data/crm-${primaryObj.toLowerCase()}`;
                              } else {
                                   // Salesforce custom object
                                   path = `/:secondId/explore-data/crm-${primaryObj.toLowerCase()}`;
                              }

                              if(![...salesForceObjects, ...customExternalObjects].some(obj => obj?.object === primaryObj)){
                                   isBlocked = true;
                              }
                         }
                         newDimensions.forEach((item) => {
                              filters.push({
                                   type: item.key,
                                   value: extractName(linkedData[item.key]),
                                   object: primaryObj,
                              });
                         });
                    }
                    break;
               default:
          }

          if(isBlocked){
               toast.error('The object is disabled, so the report ca\'t be shown. Please enable it again!')
               return;
          }
          linkedReportData = {
               filters,
               tempScopedReport,
               segmentId: segmentClick?.id,
               isNewCount,
               fieldName: fieldKey,
          };

          path = path.replace(':secondId', activeAccount.secondId) + '?staticKey=' + staticLinkKey + '&dynamicKey=' + encodeObj(linkedReportData);
          
          if (isCustomExternalPath) {
               path += '&show-custom-external-report=1';
          }
          window.open(path, '_blank');
     };

     const onClickShowDetailUser = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.userId, data: linkedData },
                    component: COMPONENT_NAME.RECORD_PROFILE_USER,
               })
          );
     };

     const onClickShowDetailEvent = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.eventId },
                    component: COMPONENT_NAME.RECORD_PROFILE_EVENT,
               })
          );
     };

     const onClickShowDetailEventECommerce = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.eventECommerceId },
                    component: COMPONENT_NAME.RECORD_PROFILE_EVENT_E_COMMERCE,
               })
          );
     };

     const onClickShowDetailSession = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.sessionId, data: linkedData },
                    component: COMPONENT_NAME.RECORD_PROFILE_SESSION,
               })
          );
     };

     const onClickShowDetailPerson = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.personId, data: linkedData },
                    component: COMPONENT_NAME.RECORD_PROFILE_PERSON,
               })
          );
     };
     const onClickShowDetailCompany = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.companyId, data: linkedData },
                    component: COMPONENT_NAME.RECORD_PROFILE_COMPANY,
               })
          );
     };

     const onClickShowDetailForm = (linkedData) => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    attributes: { id: linkedData.formId, locationForm: linkedData.formHostNamePathName || null },
                    component: COMPONENT_NAME.RECORD_PROFILE_FORM,
               })
          );
     };

     const onClickShowDetailSF = (linkedData) => {
          const object = customObjectSF || SFObject
          const dimensionIDs = dimensionsDefault.map((dimension) => linkedData[dimension.key]);

          const id = dimensionIDs.find((id) => id?.includes('-'));
          const crmId = dimensionIDs.find((id) => id && !id.includes('-'));
          let linkTab;
          fetchSalesforceDomain({ accountId: activeAccount.id, crmId, object, id: id || null }).then((domainData) => {
               if (domainData) {
                    linkTab = `${domainData.connectionData.instanceUrl}/lightning/r/${object}/${domainData.Id}/view`;
               } else {
                    linkTab = `https://login.salesforce.com/?locale=ap`;
               }
               window.open(linkTab, '_blank');
          });
     };

     const handleEnterToLink = (e) => {
          if (e.which === 13) e.currentTarget.click();
     };

     function CustomCell(props, showTooltip) {
          const originalField = getOriginalField(props.field, listSegmentReportApply);
          const refCell = useRef(null);
          let value = props.dataItem[props.field];
          const { style, className, id } = props;
          let isShowTooltipSource = true;
          let isMetricField = mergeMetrics.some((m) => props?.field.includes(m?.value));

          if (value === undefined) {
               const hasOwnProperty = props.dataItem.hasOwnProperty(props.field);
               return TableCellValueUndefined(isLoading, hasOwnProperty);
          }

          if (!showData && (props.dataIndex > 4 || tableData.length <= 5)) {
               let blur = 4;
               if (tableData.length <= 5) {
                    blur = -1;
               }
               const currentIndex = props.dataIndex < 8 ? props.dataIndex : 8;

               const intentProperty = intentProperties?.find((item) => generateCustomDimensionName(`${item?.name}_intent`) === originalField);
               if (intentProperty) {
                    return IntentPropertyCell({
                         value,
                         props,
                         originalField,
                         showTooltip,
                         refCell,
                         setRectCell,
                         intentProperty,
                         intentCellState,
                         handleRerunIconClick,
                    });
               }

               switch (originalField) {
                    case 'userSourceFirst':
                    case 'userSourceLast':
                    case 'userSourceFirstPersonCreated':
                    case 'userSourceLastPersonCreated':
                    case 'userSource':
                    case 'email':
                    case 'currentCompany':
                         return UserSourceDetails(
                              value,
                              { props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` } },
                              {},
                              showTooltip
                         );
                    case 'lifeTimeSourceFirst':
                    case 'lifeTimeSourceLast':
                    case 'closedSourceFirst':
                    case 'closedSourceLast':
                    case 'createdSourceFirst':
                    case 'createdSourceLast':
                    case 'closedChannelFirst':
                    case 'closedChannelLast':
                    case 'createdChannelFirst':
                    case 'createdChannelLast':
                    case 'lifeTimeChannelFirst':
                    case 'lifeTimeChannelLast':
                         isShowTooltipSource = false;
                         return UserSourceDetails(
                              value,
                              { ...props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` }, isShowTooltipSource },
                              {},
                              showTooltip
                         );
                    case 'userSourcePath':
                    case 'noneDirectSourcePath':
                    case 'userSourcePathPersonCreated':
                    case 'enrichType':
                    case 'emailList':
                    case 'relatedCompanies':
                    case 'dataSources':
                    case 'projects':
                    case 'recommendations':
                         if (['enrichType', 'dataSources'].includes(originalField)) {
                              value = getStatusTag(value, scoped, originalField);
                         }
                         return UserSourceDetails(
                              value,
                              { props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` } },
                              { isPath: true, refCell, originalField }
                         );
                    case 'education':
                         return GroupedItemDisplay(
                              value,
                              {
                                   style: { ...props.style, filter: `blur(${currentIndex - blur}px)` },
                                   className,
                                   id,
                              },
                              'education',
                              'schoolName'
                         );
                    case 'certifications':
                         return GroupedItemDisplay(
                              value,
                              {
                                   style: { ...props.style, filter: `blur(${currentIndex - blur}px)` },
                                   className,
                                   id,
                              },
                              'certifications',
                              'title'
                         );
                    case 'otherRoles':
                         return GroupedItemDisplay(
                              value,
                              {
                                   style: { ...props.style, filter: `blur(${currentIndex - blur}px)` },
                                   className,
                                   id,
                              },
                              'otherRoles',
                              'companyName'
                         );
                    case 'volunteering':
                         return GroupedItemDisplay(
                              value,
                              {
                                   style: { ...props.style, filter: `blur(${currentIndex - blur}px)` },
                                   className,
                                   id,
                              },
                              'volunteering',
                              'companyName'
                         );
                    case 'createdSourcePath':
                    case 'createdSourcePathND':
                    case 'lifeTimeSourcePath':
                    case 'lifeTimeSourcePathND':
                    case 'closedSourcePath':
                    case 'closedSourcePathND':
                    case 'closedChannelPath':
                    case 'closedChannelPathND':
                    case 'createdChannelPathND':
                    case 'lifeTimeChannelPath':
                    case 'lifeTimeChannelPathND':
                         isShowTooltipSource = false;
                         if(originalField === 'createdChannelPath') {  
                              return UserSourceDetails(
                                   value,
                                   { ...props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` }, isShowTooltipSource },
                                   { isPath: true, refCell }, showTooltip, false
                              );
                         } else {
                              return UserSourceDetails(
                                   value,
                                   { ...props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` }, isShowTooltipSource },
                                   { isPath: true, refCell }
                              );

                         }
                         
                    case 'industries':
                         const industries = props.dataItem.industries;
                         const newIndustries = industries ? industries.split(',') : [];

                         if (industries.toLowerCase() === 'none') {
                              return (
                                   <td
                                        {...{ style, className }}
                                        tabIndex={0}
                                        data-keyboardnavid={id}
                                        style={{ filter: `blur(${currentIndex - blur}px)` }}
                                   >
                                        <span>{value}</span>
                                   </td>
                              );
                         }
                         return (
                              <td
                                   {...{ style, className }}
                                   className={`view-detail list-tags ${className}`}
                                   style={{ filter: `blur(${currentIndex - blur}px)` }}
                              >
                                   <div className='tags-wrapper'>
                                        {newIndustries.map((item) => (
                                             <Tag item={item} />
                                        ))}
                                   </div>
                              </td>
                         );
                    default:
                         break;
               }

               return <td style={{ filter: `blur(${currentIndex - blur}px)` }}>{formatNumber(value)} </td>;
          }

          if ([REPORT_NAMES.COMPANY_DETAILS, REPORT_NAMES.PEOPLE_AND_USER_COUNT_REPORT].includes(reportName)) {
               if (!enableRevealedCompanies && (props.dataIndex > 4 || tableData.length <= 5)) {
                    let blur = 4;
                    if (tableData.length <= 5) {
                         blur = -1;
                    }
                    const currentIndex = props.dataIndex < 8 ? props.dataIndex : 8;
                    switch (originalField) {
                         case 'industries':
                              const industries = props.dataItem.industries;
                              const newIndustries = industries ? industries.split(',') : [];

                              if (industries.toLowerCase() === 'none') {
                                   return (
                                        <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                                             <span>{value}</span>
                                        </td>
                                   );
                              }
                              return (
                                   <td
                                        {...{ style, className }}
                                        className={`view-detail list-tags ${className}`}
                                        style={{ filter: `blur(${currentIndex - blur}px)` }}
                                   >
                                        <div className='tags-wrapper'>
                                             {newIndustries.map((item) => (
                                                  <Tag item={item} />
                                             ))}
                                        </div>
                                   </td>
                              );

                         case 'userSourceFirst':
                         case 'userSourceLast':
                         case 'userSourceFirstPersonCreated':
                         case 'userSourceLastPersonCreated':
                         case 'userSource':
                              return UserSourceDetails(
                                   value,
                                   { props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` } },
                                   {},
                                   showTooltip
                              );

                         case 'userSourcePath':
                         case 'noneDirectSourcePath':
                         case 'userSourcePathPersonCreated':
                              return UserSourceDetails(
                                   value,
                                   { props, style: { ...props.style, filter: `blur(${currentIndex - blur}px)` } },
                                   { isPath: true, refCell }
                              );

                         default:
                              break;
                    }

                    return <td style={{ filter: `blur(${currentIndex - blur}px)` }}>{formatNumber(value)} </td>;
               }
          }

          if (isRateField({ value: originalField, fields: propertyDynamic }) && reportType !== REPORT_TYPE.SALES_CONVERSION) {
               return (
                    <td
                         {...{ style, className }}
                         tabIndex={0}
                         data-keyboardnavid={id}
                    >{`${formatNumber(Number(value))}${value !== '' ? '%' : ''}`}</td>
               );
          }

          if (originalField.startsWith('individual') && !originalField.includes('Rate') && reportType === REPORT_TYPE.SALES_CONVERSION) {
               return <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>{`${value ? formatNumber(value) : typeof value !== 'number' ? '' : 0}`}</td>;
          }

          if (originalField.includes('Rate') && reportType === REPORT_TYPE.SALES_CONVERSION) {
               return <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>{`${value ? formatNumber(value) : typeof value !== 'number' ? '' : 0}${(value && value !== '') || typeof value === 'number' ? '%' : ''}`}</td>;
          }

          if (
               arrShowShortIds.includes(originalField) && dimensionsDefault.find((dim) => dim.key === originalField)
          ) {
               return (
                    <td
                         ref={refCell}
                         onClick={
                              value !== 'undefine'
                                   ? (event) => {
                                          showTooltip(refCell);
                                          setRectCell(event.currentTarget.getBoundingClientRect());
                                     }
                                   : null
                         }
                         {...{ className }}
                         style={{ ...style, textAlign: 'center', cursor: value !== 'undefine' ? 'pointer' : 'default' }}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         title={' '}
                         data-title={value}
                    >
                         {showShortId.includes(originalField) ? (
                              <i style={{ pointerEvents: 'none' }}>
                                   <CIcon icon='iconFingerprint' style={{ color: '#63c2de' }} />
                              </i>
                         ) : (
                              <span style={{ pointerEvents: 'none' }}>{value}</span>
                         )}
                    </td>
               );
          }

          if (originalField === 'pSegments' && listSegmentReportApply?.length) {
               const segmentsMatchedRecordIndex = [];
               listSegmentReportApply.forEach((segmentSelected, segmentSelectedIndex) => {
                    if (value?.includes(segmentSelected.id)) {
                         segmentsMatchedRecordIndex.push(segmentSelectedIndex + 1);
                    }
               });
               return (
                    <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                         <div className='custom-header-segment d-flex gap-1'>
                              {segmentsMatchedRecordIndex.map((segmentMatchedIndex) => (
                                   <div className='rule-segment-item-icon d-flex' style={{ width: 'fit-content' }}>
                                        <CIcon icon='iconChartPie' style={{ cursor: 'pointer', width: '14px', height: '14px', marginRight: '5px' }} />
                                        <span className='rule-segment-item-level'>S{segmentMatchedIndex}</span>
                                   </div>
                              ))}
                         </div>
                    </td>
               );
          }

          const customConversionValueProps =
               customDimensionInfo?.find((item) => item.properties === props.field && item.pathPropertyName === 'conversion.conversionValue');
          const conversionNameValueProps =
               conversionNameMetric?.find((item) => item.key.includes('convValue') && item.key === props.field);

          if (
               customDimensionInfo?.find(
                    (item) =>
                         item.properties === props.field &&
                         (item.pathPropertyName.includes('conversionValue') ||
                              item.pathPropertyName.includes('.value') ||
                              item.pathPropertyName.includes('.price'))
               )
          ) {
               return (
                    <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                         <span>{formatNumberValue(value)}</span>
                    </td>
               );
          }

          if (originalField.includes('ObjectCreated')) {
               return (
                    <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                         <span>{value ? formatNumberValue(Number(value)) : ''}</span>
                    </td>
               );
          }

          if (metricDynamic.some((item) => item.key === originalField && item.section === SECTION_REPORT.EXTERNAL_OBJECTS)) {
               return SessionCount(props, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
          }

          if (externalObjectMetric.some((item) => item.key === originalField)) {
               return LinkMetricCount(props, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
          }

          const intentProperty = intentProperties?.find((item) => generateCustomDimensionName(`${item?.name}_intent`) === originalField);
          if (intentProperty) {
               return IntentPropertyCell({
                    value,
                    props,
                    originalField,
                    showTooltip,
                    refCell,
                    setRectCell,
                    intentProperty,
                    intentCellState,
                    handleRerunIconClick,
               });
          }

          const dataSourceValue = props.dataItem.dataSourceValue;
          const isOnlyImport = typeof dataSourceValue === 'string' &&
          dataSourceValue !== 'None' &&
          dataSourceValue.split(',').every(s => s.trim() === 'Imported');

          switch (originalField) {
               case 'avgSessionDuration':
               case 'sessionDuration':
               case 'lifeTimeAvgSessionDuration':
                    return ReportDuration(value, props);
               case 'avgVisibleTimeOnPage':
                    return ReportDuration(value || 0, props);
               case 'avgVisibleTimePerPage':
                    return ReportDuration(value, props);
               case 'sessionCount':
               case 'lifeTimeSessionCount':
               case 'createdSessionCount':
               case 'lifeTimeUserCount':
               case 'lifeTimePeopleCount':
               case 'lifeTimeCompany':
               case 'lifeTimeLeadsCount':
               case 'lifeTimeContactsCount':
               case 'lifeTimeAccountsCount':
               case 'lifeTimeOpportunitiesCount':
               case 'lifeTimeCampaignsCount':
               case 'lifeTimeEventsCount':
               case 'lifeTimeQuotesCount':
               case 'lifeTimeOrdersCount':
               case 'lifeTimeContractsCount':
               case 'lifeTimeAccountsParentCount':
                    return SessionCount(props, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
               case 'newSessionCount':
               case 'newUserCount':
               case 'newPeopleCount':
               case 'newCompanyCount':
                    return LinkMetricCount(props, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
               case 'userCount':
                    return UserCount(props, handleEnterToLink, handleLinkToOtherReport, reportName);
               case 'revealedCompany':
               case 'companyCountActive':
               case 'companyCount':
                    return RevealedCompany(props, handleEnterToLink, handleLinkToOtherReport, reportName);
               case 'peopleCount':
               case 'peopleCountActive':
                    return PeopleCount(props, handleEnterToLink, handleLinkToOtherReport, reportName);
               case 'eventCount':
                    return EventCount(props, reportName);
               case 'personCreatedCount':
                    return PersonCreated(props, handleEnterToLink, handleLinkToOtherReport);
               case 'activeUser':
                    return ActiveUser(props, handleEnterToLink, handleLinkToOtherReport);
               case 'activeUserCount':
                    return ActiveUserCount(props, handleEnterToLink, handleLinkToOtherReport, reportName);
               case 'viewDetail':
                    return ViewDetail(
                         props.dataItem,
                         props,
                         filterDimensions,
                         onClickShowDetailEvent,
                         onClickShowDetailEventECommerce,
                         onClickShowDetailUser,
                         onClickShowDetailSession,
                         onClickShowDetailPerson,
                         onClickShowDetailForm,
                         onClickShowDetailCompany,
                         onClickShowDetailSF
                    );
               case 'sessionId':
               case 'userId':
               case 'personId':
               case 'formId':
               case 'companyId':
                    return ViewProfileById(
                         props.dataItem,
                         props,
                         filterDimensions,
                         onClickShowDetailUser,
                         onClickShowDetailSession,
                         onClickShowDetailPerson,
                         onClickShowDetailForm,
                         onClickShowDetailCompany,
                         props.field,
                         originalField
                    );
               case 'userSourceFirst':
               case 'userSourceLast':
               case 'noneDirectSourcePath':
               case 'userSourceFirstPersonCreated':
               case 'userSourcePathPersonCreated':
               case 'userSourceLastPersonCreated':
               case 'userSource':
               case 'currentCompany':
               case 'userSourceFirstND':
               case 'userSourceLastND':
               case 'userSourcePathND':
                    return UserSourceDetails(value, props, {}, showTooltip);
               case 'email':
                    if (!activeAccount.validateEmailStatus || isOnlyImport) {
                         if (typeof value === 'object' && value !== null) {
                              delete value.qualityScore;
                              delete value.roleEmail;
                              delete value.catchAllEmail;
                              delete value.disposableEmail;
                              delete value.valid;

                              if (!isOnlyImport) {
                                   value.noted = `Email Validation is Disabled. Please contact support <br />if you're interested in enabling data validation & enrichment!`;
                              }
                         }
                    }
                    return UserSourceDetails(value, props, {}, showTooltip);
               case 'lifeTimeSourceFirst':
               case 'lifeTimeSourceLast':
               case 'closedSourceFirst':
               case 'closedSourceLast':
               case 'createdSourceFirst':
               case 'createdSourceLast':
               case 'closedChannelFirst':
               case 'closedChannelLast':
               case 'createdChannelFirst':
               case 'createdChannelLast':
               case 'lifeTimeChannelFirst':
               case 'lifeTimeChannelLast':
                    isShowTooltipSource = false;
                    return UserSourceDetails(value, { ...props, isShowTooltipSource }, {}, showTooltip);
               case 'userSourcePath':
                    return UserSourceDetails(value, props, { isPath: true, refCell, originalField }, showTooltip);
               case 'enrichType':
               case 'relatedCompanies':
               case 'dataSources':
               case 'projects':
               case 'recommendations':
                    if (['enrichType', 'dataSources'].includes(originalField)) {
                         value = getStatusTag(value, scoped, originalField);
                    }
                    return UserSourceDetails(value, props, { isPath: true, refCell, originalField });
               case 'targetAccountGroups':
                    // string to array of object with name key
                    isShowTooltipSource = false;
                    value = typeof value === 'string' && value !== 'None'
                              ? value.split(',').map(item => ({ name: item.trim().replace(/'/g, '') })) 
                              : [];
                    return UserSourceDetails(value, { ...props, isShowTooltipSource }, { isPath: true, refCell, originalField });
               case 'education':
                    return GroupedItemDisplay(value, { style, className, id }, 'education', 'schoolName');
               case 'certifications':
                    return GroupedItemDisplay(value, { style, className, id }, 'certifications', 'title');
               case 'otherRoles':
                    return GroupedItemDisplay(value, { style, className, id }, 'otherRoles', 'companyName');
               case 'volunteering':
                    return GroupedItemDisplay(value, { style, className, id }, 'volunteering', 'companyName');
               case 'emailList':
                    if (!activeAccount.validateEmailStatus || isOnlyImport) {
                         if (value && Array.isArray(value)) {
                              value.forEach((itemValue) => {
                                   if (typeof itemValue === 'object' && itemValue !== null) {
                                        delete itemValue.qualityScore;
                                        delete itemValue.roleEmail;
                                        delete itemValue.catchAllEmail;
                                        delete itemValue.disposableEmail;
                                        delete itemValue.valid;
                                        delete itemValue.currentB2BRole;

                                        if (!isOnlyImport) {
                                             itemValue.noted = `Email Validation is Disabled. Please contact support <br />if you're interested in enabling data validation & enrichment!`;
                                        }
                                   }
                              });
                         }
                    }
                    return UserSourceDetails(value, props, { isPath: true, refCell, originalField });
               case 'createdSourcePath':
               case 'createdSourcePathND':
               case 'lifeTimeSourcePath':
               case 'lifeTimeSourcePathND':
               case 'closedSourcePath':
               case 'closedSourcePathND':
               case 'closedChannelPath':
               case 'closedChannelPathND':
               case 'createdChannelPath':
               case 'createdChannelPathND':
               case 'lifeTimeChannelPath':
               case 'lifeTimeChannelPathND':
                    isShowTooltipSource = false;
                    if(originalField === 'createdChannelPath') {  
                         return UserSourceDetails(value, { ...props, isShowTooltipSource }, { isPath: true, refCell }, showTooltip, false);
                    } else {
                         return UserSourceDetails(value, { ...props, isShowTooltipSource }, { isPath: true, refCell });
                    }
               case 'skills':
                    return TagArrayFormat(value, props, originalField);
               case 'industries':
                    return Industries(value, props);
               case 'priceCustom':
               case 'conversionValue':
                    return ConversionValue(value, props);
               case `${customConversionValueProps}`:
                    return CustomConversionValue(value, props);
               case 'engagementDriven':
               case 'firstTouch':
               case 'lastTouch':
               case 'linear':
               case 'uShape':
               case 'conversionValueCount':
               case 'primaryConversionValueCount':
               case 'primaryConversionCount':
               case 'secondaryConversionCount':
               case `${conversionNameValueProps ? conversionNameValueProps.key : ``}`:
               case 'secondaryConversionValueCount':
                    return SecondaryConversionValueCount(value, props);
               // Clickable conversionCount (By Conversion only)
               case 'conversionCount':
                    if (reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION) {
                         return SalesConversionCount(props, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
                    }
                    return SecondaryConversionValueCount(value, props);
               case 'funnelOrder':
                    return (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              <span>{value ? String(value) : 0}</span>
                         </td>
                    );
               default:
                    return TableCellDefault(
                         value,
                         { ...props, className: `${className} field-type-string` },
                         refCell,
                         showTooltip,
                         setRectCell,
                         isMetricField
                    );
          }
     }

     CustomCell.propTypes = {
          field: PropTypes.any,
          dataItem: PropTypes.shape({
               dataSourceValue: PropTypes.string,
          }),
     };

     const propsCompare = {
          getWidthByKey,
          metricDefault,
          convName,
          eCommerce,
          convRate,
          tableGrid,
          handleColumnResize,
          SkeletonLoader,
          lock,
          activePage,
          setActivePage,
          arrShowShortIds,
          showLock,
          showShortId,
          sort,
          disableSort,
          setLock,
          setShowShortId,
     };

     const shouldDisplayUpgradePlanText = () => {
          return (
               (!showData && !isLoading) ||
               ((reportName === REPORT_NAMES.COMPANY_DETAILS || reportName === REPORT_NAMES.PEOPLE_AND_USER_COUNT_REPORT) &&
                    !enableRevealedCompanies &&
                    !isLoading)
          );
     };
     
     const mergeMetrics = useMemo(() => [...filterMetric], [filterMetric, metricsDefault]); // eslint-disable-line
     const uniqByMetrics = uniqBy(mergeMetrics, 'label');

     const handleColumnReorder = debounce(({ columns, columnId }) => {
          columns.forEach((column) => {
               column.children.some((child) => {
                    if (child.id === columnId) {
                         const sortedChildren = [...column.children].sort((a, b) => a.orderIndex - b.orderIndex);
                         const filterChildren = sortedChildren.filter((_child) => _child.title === child.title);

                         const beforeChildren = sortedChildren.filter(
                              (_child) => _child.orderIndex < child.orderIndex && _child.title !== child.title
                         );
                         const afterChildren = sortedChildren.filter(
                              (_child) => _child.orderIndex > child.orderIndex && _child.title !== child.title
                         );

                         const reorderedColumnsCustom = [...beforeChildren, ...filterChildren, ...afterChildren];

                         const reorderedColumns = reorderedColumnsCustom.map((child) => ({
                              label: child.title,
                              value: convertNameSegmentSort({ sort: { fieldName: child.field }, listSegmentReportApply }),
                         }));

                         switch (column.title) {
                              case 'Dimensions':
                                   setFilterDimensions(reorderedColumns);
                                   handleFindView(REPORT_TAB_TYPES.DIMENSIONS, reorderedColumns, false);
                                   break;
                              case 'Properties':
                                   setFilterProperties(reorderedColumns);
                                   handleFindView(REPORT_TAB_TYPES.PROPERTIES, reorderedColumns, false);
                                   break;
                              case 'Metrics':
                                   setFilterMetric(uniqBy(reorderedColumns, 'label'));
                                   handleFindView(REPORT_TAB_TYPES.METRICS, reorderedColumns, false);
                                   break;
                              default:
                                   break;
                         }

                         return true;
                    }

                    return false;
               });
          });
     }, 20);
     return (
          <div className={`report-table ${!showData ? 'no-action' : ''} ${isResizing ? 'table-resizing' : ''}`}>
               {filterEvent?.length > 0 && filterEvent.some((item) => item?.clickable) && reportType === REPORT_TYPE.SALE_FORCE ? (
                    <div>
                         <p className='text-left mb-0' style={{ color: '#000', fontSize: '14px', paddingLeft: '20px' }}>
                              If data is missing as the record isn't yet in ListenLayer, but the relationship was still found and displayed.
                         </p>
                    </div>
               ) : (
                    ``
               )}
               {isRebuildLoading ? (
                    <RebuildSegmentLoading />
               ) : (
                    <>
                         {changeData && tableData.length !== 0 && <ChangeDataLoading />}
                         {(reportIsCancel || reportIsLargeRecords) && (
                              <ReportIsCancel
                                   setReportIsCancel={setReportIsCancel}
                                   setReloadTable={setReloadTable}
                                   reportIsLargeRecords={reportIsLargeRecords}
                              />
                         )}
                         {isLoadingFetch && showLongLoading && <LongLoading />}
                    </>
               )}
               {isLoading && pagination && <LoadingPanel />}
               {listSegmentApplyReload && <LoadingPanel />}
               {shouldDisplayUpgradePlanText() && <UpgradePlanText tableData={tableData} />}

               {!reportCompare.rows && (
                    <>
                         <Tooltip
                              className={`${recordProfile ? 'tooltip-profile' : ''}`}
                              onPosition={changePositionTooltip}
                              tooltipStyle={{ background: '#050505' }}
                              content={(props) => <TooltipContentTemplate props={props} showCopyTooltip={showCopyTooltip} />}
                              openDelay={0}
                              open={openTooltip}
                              targetElement={targetElement}
                              position={positionTooltip}
                         >
                              <Grid
                                   ref={tableGrid}
                                   style={{ width: 'fit-content', maxWidth: '100%', maxHeight: pagination ? '2500px' : '800px' }}
                                   data={tableData.length === 0 && (isLoading || isLoadingTable) ? Array(5).fill({}) : tableData}
                                   onScroll={(event) => {
                                        removeElementTooltipScroll();
                                        if (!pagination) {
                                             debouncedHandleScrollTable(event);
                                        }
                                        if (openTooltip) {
                                             setOpenTooltip(false);
                                        }
                                   }}
                                   resizable={true}
                                   reorderable={!isLoading}
                                   navigatable={true}
                                   scrollable='scrollable'
                                   onColumnReorder={handleColumnReorder}
                                   onColumnResize={handleColumnResize}
                              >
                                   <GridNoRecords>No results found!</GridNoRecords>

                                   {filterDimensions.length > 0 && (
                                        <GridColumn orderIndex={0} title='Dimensions' resizable={false}>
                                             {filterDimensions.map((item, index) => {
                                                  return (
                                                       <GridColumn
                                                            key={index}
                                                            title={item.label}
                                                            field={item.value}
                                                            orderIndex={index}
                                                            width={getWidthByKey({
                                                                 data: widthColumnReports,
                                                                 reportType,
                                                                 reportName,
                                                                 label: item.label,
                                                                 key: item.value,
                                                                 tableData,
                                                                 index,
                                                            })}
                                                            headerCell={(props) => (
                                                                 <CustomHeaderCell
                                                                      props={props}
                                                                      dimensions={dimensionsDefault}
                                                                      arrShowShortIds={arrShowShortIds}
                                                                      showLock={showLock}
                                                                      lock={lock}
                                                                      showShortId={showShortId}
                                                                      sort={sort}
                                                                      disableSort={disableSort}
                                                                      setLock={setLock}
                                                                      setShowShortId={setShowShortId}
                                                                      setOpenTooltip={setOpenTooltip}
                                                                 />
                                                            )}
                                                            locked={!!lock[item.value]}
                                                            footerCell={(props) => (
                                                                 <CustomFooterCellDimensions
                                                                      props={props}
                                                                      filterDimensions={filterDimensions}
                                                                      filterProperties={filterProperties}
                                                                 />
                                                            )}
                                                            cell={(props) => CustomCell(props, showTooltip)}
                                                       />
                                                  );
                                             })}
                                        </GridColumn>
                                   )}

                                   {filterProperties.length > 0 && (
                                        <GridColumn orderIndex={1} title='Properties' resizable={false}>
                                             {filterProperties.map((item, index) => {
                                                  return (
                                                       <GridColumn
                                                            key={index}
                                                            title={item.label}
                                                            field={item.value}
                                                            orderIndex={index}
                                                            width={getWidthByKey({
                                                                 data: widthColumnReports,
                                                                 reportType,
                                                                 reportName,
                                                                 label: item.label,
                                                                 key: item.value,
                                                            })}
                                                            headerCell={(props) => (
                                                                 <CustomHeaderCell
                                                                      props={props}
                                                                      dimensions={dimensionsDefault}
                                                                      arrShowShortIds={arrShowShortIds}
                                                                      showLock={showLock}
                                                                      lock={lock}
                                                                      showShortId={showShortId}
                                                                      sort={sort}
                                                                      disableSort={disableSort}
                                                                      setLock={setLock}
                                                                      setShowShortId={setShowShortId}
                                                                 />
                                                            )}
                                                            locked={!!lock[item.value]}
                                                            footerCell={(props) => (
                                                                 <CustomFooterCellProperties
                                                                      props={props}
                                                                      filterDimensions={filterDimensions}
                                                                      filterProperties={filterProperties}
                                                                 />
                                                            )}
                                                            cell={(props) => CustomCell(props, showTooltip)}
                                                       />
                                                  );
                                             })}
                                        </GridColumn>
                                   )}

                                   {mergeMetrics.length > 0 &&
                                        (listSegmentReportApply.length > 0 ? (
                                             <GridColumn orderIndex={2} title='Metrics' resizable={false}>
                                                  {uniqByMetrics.flatMap((metric, metricIndex) =>
                                                       listSegmentReportApply.map((seg, segIndex) => {
                                                            const valueSegment = idSegment(metric.value.split('_')[0])
                                                                 ? metric.value.replace(metric.value.split('_')[0] + '_', '')
                                                                 : metric.value;

                                                            const item = {
                                                                 label: metric.label,
                                                                 value: `${getSegmentField(seg.id, valueSegment)}`,
                                                            };

                                                            const orderIndex = metricIndex * listSegmentReportApply.length + (segIndex + 1);

                                                            return (
                                                                 <GridColumn
                                                                      key={`${item.value}_${orderIndex}`}
                                                                      title={item.label}
                                                                      field={item.value}
                                                                      orderIndex={orderIndex}
                                                                      width={getWidthByKey({
                                                                           data: widthColumnReports,
                                                                           reportType,
                                                                           reportName,
                                                                           label: item.label,
                                                                           key: item.value,
                                                                           tableData,
                                                                           index: orderIndex,
                                                                           isMetric: true,
                                                                      })}
                                                                      headerCell={(props) => (
                                                                           <div className='custom-header-segment d-flex align-items-center justify-content-between'>
                                                                                <CustomHeaderCell
                                                                                     props={props}
                                                                                     dimensions={dimensionsDefault}
                                                                                     arrShowShortIds={arrShowShortIds}
                                                                                     showLock={showLock}
                                                                                     lock={lock}
                                                                                     showShortId={showShortId}
                                                                                     sort={sort}
                                                                                     disableSort={disableSort}
                                                                                     setLock={setLock}
                                                                                     setShowShortId={setShowShortId}
                                                                                />
                                                                                <div className='rule-segment-item-icon d-flex'>
                                                                                     <CIcon
                                                                                          icon='iconChartPie'
                                                                                          style={{
                                                                                               cursor: 'pointer',
                                                                                               width: '14px',
                                                                                               height: '14px',
                                                                                               marginRight: '5px',
                                                                                          }}
                                                                                     />
                                                                                     <span className='rule-segment-item-level'>S{segIndex + 1}</span>
                                                                                </div>
                                                                           </div>
                                                                      )}
                                                                      locked={!!lock[item.value]}
                                                                      footerCell={(props) => (
                                                                           <CustomFooterCellDefault props={props} reportData={reportData} />
                                                                      )}
                                                                      cell={(props) => CustomCell(props, showTooltip)}
                                                                 />
                                                            );
                                                       })
                                                  )}
                                             </GridColumn>
                                        ) : (
                                             <GridColumn orderIndex={2} title='Metrics' resizable={false}>
                                                  {mergeMetrics.map((item, index) => {
                                                       return (
                                                            <GridColumn
                                                                 key={index}
                                                                 title={item.label}
                                                                 field={item.value}
                                                                 orderIndex={index}
                                                                 width={getWidthByKey({
                                                                      data: widthColumnReports,
                                                                      reportType,
                                                                      reportName,
                                                                      label: item.label,
                                                                 })}
                                                                 headerCell={(props) => (
                                                                      <CustomHeaderCell
                                                                           props={props}
                                                                           dimensions={dimensionsDefault}
                                                                           arrShowShortIds={arrShowShortIds}
                                                                           showLock={showLock}
                                                                           lock={lock}
                                                                           showShortId={showShortId}
                                                                           sort={sort}
                                                                           disableSort={disableSort}
                                                                           setLock={setLock}
                                                                           setShowShortId={setShowShortId}
                                                                      />
                                                                 )}
                                                                 locked={!!lock[item.value]}
                                                                 footerCell={(props) => (
                                                                      <CustomFooterCellDefault props={props} reportData={reportData} />
                                                                 )}
                                                                 cell={(props) => CustomCell(props, showTooltip)}
                                                            />
                                                       );
                                                  })}
                                             </GridColumn>
                                        ))}
                              </Grid>
                         </Tooltip>
                         {(!recordProfile || (recordProfile && reportData.overallRowCount > 5)) && (
                              <PaginationReport
                                   pagination={pagination}
                                   activePage={activePage}
                                   totalPages={totalPages}
                                   onPaginationChange={onPaginationChange}
                                   onChangeItemsPerPage={onChangeItemsPerPage}
                                   itemsPerPage={itemsPerPage}
                                   handleChangePagination={handleChangePagination}
                                   totalRow={reportData.overallRowCount}
                                   isExploreReport={isExploreReport}
                              />
                         )}
                    </>
               )}

               {reportCompare.rows && <ReportTableCompare {...propsCompare}></ReportTableCompare>}

          </div>
     );
};

ReportTable.propTypes = {
     mouseLeft: PropTypes.any,
     element: PropTypes.object,
     mouseTop: PropTypes.any,
     targetColumnId: PropTypes.any,
     columns: PropTypes.array,
     end: PropTypes.any,
};

export default React.memo(ReportTable);
